import React, { useCallback } from "react";
import styles from "./ManageCodes.module.scss";
import { useTranslation } from "react-i18next";
import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable, toJS } from "mobx";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import { observer } from "mobx-react-lite";
import type { CountriesType } from "@whatslly/commons/phoneUtils/countries";
import { COUNTRIES } from "@whatslly/commons/phoneUtils/countries";
import { SearchInput } from "@whatslly/ui-components/v2/BaseElements/SearchInput";
import { CheckBox } from "@whatslly/ui-components/v2/BaseElements/CheckBox";
import { Button } from "@whatslly/ui-components/v2/Button";
import { useNavigate } from "react-router-dom";
import sortBy from "lodash/sortBy";
import { LOADER_IMG } from "@whatslly/commons/constants";
import type { IManageCodesService } from "@whatslly/ui-components/services/ManageCodesService";
import { InjectManageCodesService } from "@whatslly/ui-components/services/ManageCodesService";
import classNames from "classnames";
import { ErrorBox } from "@whatslly/ui-components/ErrorBox";
import { AsyncImage } from "@whatslly/ui-components/AsyncImage";

const MAXIMUM_SELECTED_COUNTRIES = 5;

type CountriesTypeSelected = CountriesType & { selected: boolean };

@injectable()
class ManageCodesStore {
  isSaving = false;
  search = "";
  selectedCountries: IPromiseBasedObservable<Map<string, CountriesTypeSelected> | undefined> = fromPromise.resolve(
    new Map<string, CountriesTypeSelected>()
  );

  constructor(@inject(InjectManageCodesService) private readonly manageCodesService: IManageCodesService) {
    makeObservable(this, {
      selectedCountries: observable.ref,
      search: observable,
      countries: computed,
      save: action.bound,
      onSearch: action.bound,
      toggleCountry: action.bound,
      setIsSaving: action,
      isSaving: observable,
    });
    this.selectedCountries = fromPromise(this.manageCodesService.getSelectedCountries().then((res) => this.codesToMap(res.countries)));
  }

  private codesToMap(codes: { country: string; code: string }[]): Map<string, CountriesTypeSelected> {
    const map = (codes || [])
      .map((item) => {
        const country = COUNTRIES.find((c) => c.code === item.code && c.country === item.country);
        return [country?.country, country ? { ...country, selected: true } : null];
      })
      .filter(([country]) => Boolean(country));
    return observable.map(new Map(map as [string, CountriesTypeSelected][]));
  }

  onSearch(search: string) {
    this.search = search;
  }

  async toggleCountry(country: CountriesTypeSelected) {
    const selected = await this.selectedCountries;
    country.selected = !country.selected;
    if (country.selected) {
      selected?.set(country.country, country);
    } else {
      selected?.delete(country.country);
    }
  }

  get countries() {
    if (this.selectedCountries.state !== "fulfilled") {
      return [];
    }
    const search = this.search.toLowerCase();
    const selected = this.selectedCountries.value;
    let arr = COUNTRIES;
    if (search) {
      arr = arr.filter((country) => country.country.toLowerCase().includes(search) || country.text.toLowerCase().includes(search));
    }
    return sortBy(
      arr.map((country) => ({ ...country, selected: selected?.has(country.country) ?? false })),
      `country`
    );
  }

  async save() {
    const selectedCountries = await this.selectedCountries;
    await this.manageCodesService.saveCodes(toJS(selectedCountries));
  }

  setIsSaving(val: boolean) {
    this.isSaving = val;
  }
}

const Country: React.FC<{
  dataTestId: string;
  country: CountriesTypeSelected;
  onChange: (country: CountriesTypeSelected) => void;
  isDisabled: boolean;
  lang: string;
}> = React.memo(({ dataTestId, country, onChange, isDisabled, lang }) => {
  const es = lang === "es" ? country.countryES : country.country;
  const countryText = lang === "pt" ? country.countryPT : es;

  return (
    <label className={styles.country}>
      <div className={styles.checkbox}>
        <CheckBox data-testid={dataTestId} checked={country.selected} onChange={() => onChange(country)} disabled={isDisabled} />
      </div>
      <span>
        {countryText} ({country.text})
      </span>
    </label>
  );
});

export const ManageCodes: React.FC<{
  inputWriteBackground?: boolean;
  hideFooter?: boolean;
  onSave?: (data: { success: boolean }) => void;
  OverrideErrorBox?: React.FC<{ error: string; testId?: string }>;
}> = observer(({ inputWriteBackground, hideFooter, onSave, OverrideErrorBox }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const store = useLocalStoreWithInjectedDependencies(ManageCodesStore);

  const onClickOnSave = useCallback(async () => {
    try {
      store.setIsSaving(true);
      await store.save();
      onSave?.({ success: true });
    } catch (err) {
      onSave?.({ success: false });
    } finally {
      store.setIsSaving(false);
    }
  }, [onSave, store]);

  return (
    <>
      {store.selectedCountries.case({
        rejected: (error) => {
          return OverrideErrorBox ? (
            <OverrideErrorBox error={error.message} testId={`manage-code-override-error-box`} />
          ) : (
            <ErrorBox error={error.message} testId={`manage-code-error-box`} />
          );
        },
        pending: () => (
          <div className={styles.loaderWrapper}>
            <div className={styles.loader}>
              <AsyncImage alt="tuvis loader" src={LOADER_IMG} />
              <div>{t("country_codes.loading")}</div>
            </div>
          </div>
        ),
        fulfilled: (selected) => {
          const isMaxed = (selected?.size ?? 0) >= MAXIMUM_SELECTED_COUNTRIES;
          return (
            <div className={styles.manageCodes}>
              <div className={classNames({ [styles.header]: hideFooter })}>
                <h2 className={styles.title}>{t("country_codes.manage_codes_subtitle")}</h2>
                {hideFooter && (
                  <Button
                    isSubmitting={store.isSaving}
                    disabled={store.isSaving}
                    paddingInline="small"
                    onClick={onClickOnSave}
                    testId={`save-settings`}
                  >
                    {t("country_codes.save")}
                  </Button>
                )}
              </div>
              <span className={styles.description}>{t("country_codes.manage_codes_desctiption")}</span>
              <SearchInput
                data-testid={`search-country-codes`}
                className={classNames(styles.search, { [styles.searchWhiteBackgroundColor]: inputWriteBackground })}
                inputClassName={classNames({ [styles.searchWhiteBackgroundColor]: inputWriteBackground })}
                value={store.search}
                clearAllContainerClassName={styles.clearAllContainer}
                onChange={store.onSearch}
                placeholder={t("country_codes.search_countries")}
              />

              <div className={styles.countriesList}>
                {store.search === "" && (
                  <div className={styles.selectedCountries}>
                    {Array.from(selected?.values() ?? []).map((country) => (
                      <Country
                        dataTestId={`${country.country.toLowerCase().replaceAll(" ", "_")}-selected`}
                        key={country.country}
                        country={country}
                        onChange={store.toggleCountry}
                        isDisabled={false}
                        lang={i18n.language}
                      />
                    ))}
                  </div>
                )}
                {store.countries.map((country) => (
                  <Country
                    dataTestId={`${country.country.toLowerCase().replaceAll(" ", "_")}`}
                    key={country.country + country.selected}
                    country={country}
                    onChange={store.toggleCountry}
                    isDisabled={isMaxed && !country.selected}
                    lang={i18n.language}
                  />
                ))}
              </div>
              {isMaxed && <div className={styles.maxCountries}>{t("country_codes.max_countries_warning")}</div>}
            </div>
          );
        },
      })}
      {!hideFooter && (
        <div className={styles.footer}>
          <span onClick={() => navigate(-1)} className={styles.cancel}>
            {t("actions_cancel")}
          </span>
          <Button isSubmitting={store.isSaving} disabled={store.isSaving} paddingInline="small" onClick={onClickOnSave} testId={`save-settings`}>
            {t("country_codes.save")}
          </Button>
        </div>
      )}
    </>
  );
});
